import { useEffect, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Input from "@/v2-ui/input"
import Switch from "@/v2-ui/switch"
import Textarea from "@/v2-ui/textarea"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeConfigPlatformUpsertMutation } from "@/v2-backoffice-client/uptime/configPlatform/upsert/uptime.configPlatform.upsert.mutation"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type Platform = UptimeConfigQuery["getStatusConfig"]["Platforms"][number]

export type UptimeConfigPlatformDialogProps = DialogProps & {
  mode: "create" | "edit"
  platform?: Platform | null
}

const FORM_ID = "uptime-config-platform-form"

function makeInitial(platform: Platform | null | undefined) {
  return {
    id: platform?.Id ?? "",
    name: platform?.Name ?? "",
    description: platform?.Description ?? "",
    priority: platform?.Priority ?? 0,
    isEnabled: platform?.IsEnabled ?? true,
  }
}

// UptimeConfigPlatformDialog handles both creation and edit. We
// don't expose a separate Create dialog because the field set is
// identical -- the only difference is whether `Id` is editable
// (create) or locked (edit, since the backend keys on Id and we
// don't expose a rename path).
function UptimeConfigPlatformDialog(props: UptimeConfigPlatformDialogProps) {
  const { mode, platform, open, onOpenChange, ...dialogProps } = props

  const [form, setForm] = useState(() => makeInitial(platform))
  const [upsert, { loading, error, reset }] =
    useUptimeConfigPlatformUpsertMutation({
      onCompleted() {
        onOpenChange?.(false)
      },
    })

  useEffect(() => {
    if (open) {
      setForm(makeInitial(platform))
      reset()
    }
  }, [open, platform, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {mode === "edit" ? "Update Platform" : "New Platform"}
          </Dialog.Title>
          <Dialog.Description>
            Platforms are the top-level cards on the public status page.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-3" error={error} />

        <form
          id={FORM_ID}
          className="uptime-config-form"
          onSubmit={(e) => {
            e.preventDefault()
            upsert({
              variables: {
                input: {
                  id: form.id.trim(),
                  name: form.name.trim(),
                  description:
                    form.description.trim() === ""
                      ? null
                      : form.description.trim(),
                  isEnabled: form.isEnabled,
                  priority: form.priority,
                },
              },
            })
          }}
        >
          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-platform-id">ID</Label>
            <Input
              id="uptime-config-platform-id"
              type="text"
              required
              autoFocus={mode === "create"}
              disabled={mode === "edit"}
              placeholder="e.g. placepoint-v2"
              value={form.id}
              onChange={(e) => {
                setForm({ ...form, id: e.target.value })
              }}
            />
            <p className="uptime-config-form-hint">
              Stable kebab-case key. Cannot be changed after create.
            </p>
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-platform-name">Display name</Label>
            <Input
              id="uptime-config-platform-name"
              type="text"
              required
              placeholder="e.g. Placepoint"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value })
              }}
            />
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-platform-description">
              Description
            </Label>
            <Textarea
              id="uptime-config-platform-description"
              rows={2}
              placeholder="Short customer-facing description (optional)"
              value={form.description}
              onChange={(e) => {
                setForm({ ...form, description: e.target.value })
              }}
            />
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-platform-priority">Priority</Label>
            <Input
              id="uptime-config-platform-priority"
              type="number"
              required
              value={form.priority}
              onChange={(e) => {
                setForm({
                  ...form,
                  priority: Number.parseInt(e.target.value, 10) || 0,
                })
              }}
            />
            <p className="uptime-config-form-hint">
              Lower priority renders first.
            </p>
          </div>

          <Label
            className="uptime-config-form-toggle"
            htmlFor="uptime-config-platform-enabled"
          >
            <span>Enabled</span>
            <Switch
              id="uptime-config-platform-enabled"
              checked={form.isEnabled}
              onCheckedChange={(checked) => {
                setForm({ ...form, isEnabled: checked === true })
              }}
            />
          </Label>
        </form>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} isLoading={loading}>
            {mode === "edit" ? "Save" : "Create"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UptimeConfigPlatformDialog
